'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { BUCKET_LABELS, isBucketId } from '@/lib/ar/statuses'

// ============================================================================
// Page-aware context (ported from AP lib/ai/page-context.ts, AR routes)
// ============================================================================

export type PageKind =
  | 'briefing'
  | 'dashboard'
  | 'explorer'
  | 'workbench'
  | 'helpdesk'
  | 'policies'
  | 'insights'
  | 'agent-hub'
  | 'data-manager'
  | 'other'

export interface PageContextInput {
  page: string
  invoiceNumber?: string | null
  queue?: string | null
  ticketNumber?: string | null
  highlight?: string | null
  /** Workbench actions the open invoice allows now (blocked ones removed); null when unknown. */
  allowedActions?: readonly string[] | null
}

export function pageKind(page: string): PageKind {
  const p = (page || '/').split('?')[0]
  if (p === '/' || p.startsWith('/briefing')) return 'briefing'
  if (p.startsWith('/dashboard')) return 'dashboard'
  if (p.startsWith('/receivables') || p.startsWith('/explorer') || p.startsWith('/invoices')) return 'explorer'
  if (p.startsWith('/workbench')) return 'workbench'
  if (p.startsWith('/queries')) return 'helpdesk'
  if (p.startsWith('/ai/policies')) return 'policies'
  if (p.startsWith('/ai/insights')) return 'insights'
  if (p.startsWith('/agent-hub')) return 'agent-hub'
  if (p.startsWith('/data-manager')) return 'data-manager'
  return 'other'
}

const KIND_LABELS: Record<PageKind, string> = {
  briefing: 'Command Briefing',
  dashboard: 'Dashboard',
  explorer: 'Explorer',
  workbench: 'AI Workbench',
  helpdesk: 'Help Desk',
  policies: 'AI Policies',
  insights: 'AI Insights',
  'agent-hub': 'Agent Hub',
  'data-manager': 'Data Manager',
  other: '',
}

export function pageLabel(page: string): string {
  const kind = pageKind(page)
  if (kind !== 'other') return KIND_LABELS[kind]
  const path = (page || '/').split('?')[0]
  const segments = path.split('/').filter(Boolean)
  const last = segments[segments.length - 1] || 'Command Briefing'
  return last
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')
}

/** Context chip parts: page label, then invoice or ticket, then bucket. */
export function contextChipParts(ctx: PageContextInput): string[] {
  const parts = [pageLabel(ctx.page)]
  if (ctx.invoiceNumber) parts.push(ctx.invoiceNumber)
  if (ctx.ticketNumber) parts.push(ctx.ticketNumber)
  if (ctx.highlight) parts.push(ctx.highlight)
  if (ctx.queue && isBucketId(ctx.queue) && ctx.queue !== 'all') parts.push(BUCKET_LABELS[ctx.queue])
  return parts.filter(Boolean)
}

export interface Starter {
  icon: React.ElementType
  label: string
  query: string
}

/** Starter prompts by page (plan 12 section 8.2). */
export function startersFor(ctx: PageContextInput): Starter[] {
  const kind = pageKind(ctx.page)
  switch (kind) {
    case 'briefing':
      return [
        { icon: Icons.alertCircle, label: 'What needs review first?', query: 'What needs my review first?' },
        { icon: Icons.activity, label: 'Since yesterday', query: 'What changed since yesterday?' },
        { icon: Icons.users, label: 'Customers with most overdue', query: 'Which customers have the most overdue balance?' },
        { icon: Icons.fileText, label: 'Aging report PDF', query: 'Generate an aging report as a PDF.' },
      ]
    case 'dashboard':
      return [
        { icon: Icons.activity, label: 'Summarize the KPIs', query: 'Summarize the current AR KPIs and what needs my attention.' },
        { icon: Icons.clock, label: 'Aging profile', query: 'What is our aging profile?' },
        { icon: Icons.trendingUp, label: 'Cash forecast', query: 'What cash do we expect in the next 30 days?' },
        { icon: Icons.target, label: 'Collection effectiveness', query: 'How effective were collections this month?' },
      ]
    case 'explorer':
      return [
        { icon: Icons.layers, label: 'Count by bucket', query: 'How many invoices are in each bucket?' },
        { icon: Icons.clock, label: 'Oldest in review', query: 'Show me the oldest invoices that still need review.' },
        { icon: Icons.alertCircle, label: 'Overdue over EUR 10,000', query: 'Show me overdue invoices over EUR 10,000.' },
        { icon: Icons.fileText, label: 'Open deductions', query: 'List open deductions and which look invalid.' },
      ]
    case 'workbench': {
      // Action starters only when the open invoice allows that action now: a
      // draft in review (not yet issued) never offers a payment reminder.
      const allowed = ctx.allowedActions ?? []
      return [
        { icon: Icons.fileText, label: 'Summarize this invoice', query: 'Summarize this invoice and tell me if anything looks off.' },
        { icon: Icons.helpCircle, label: 'Why does it need review?', query: 'Why does this invoice need review? Reference the policies involved.' },
        { icon: Icons.download, label: 'Show sales order and POD', query: 'Show me the sales order and proof of delivery for this invoice.' },
        ...(allowed.includes('remind')
          ? [{ icon: Icons.mail, label: 'Draft a reminder', query: 'Draft a payment reminder to the customer in their language.' }]
          : []),
        ...(allowed.includes('hold')
          ? [{ icon: Icons.clock, label: 'Put on hold', query: 'Put this invoice on hold for 7 days.' }]
          : []),
        { icon: Icons.download, label: 'Audit report PDF', query: 'Give me the audit report for this invoice.' },
      ]
    }
    case 'helpdesk':
      return [
        { icon: Icons.messageSquare, label: 'Summarize this ticket', query: "Summarise this ticket and the customer's request." },
        { icon: Icons.alertCircle, label: 'Open disputes', query: 'Which dispute tickets are open?' },
        { icon: Icons.info, label: 'Explain this page', query: 'Explain what the Help Desk shows.' },
      ]
    case 'policies':
      return [
        ...(ctx.highlight
          ? [{ icon: Icons.info, label: 'Explain this policy', query: `Explain policy ${ctx.highlight} and when it applies.` }]
          : []),
        { icon: Icons.brain, label: 'Create a policy', query: 'Help me create a new AR policy.' },
        { icon: Icons.activity, label: 'Most-used policies', query: 'Which policies applied most often this month?' },
      ]
    case 'insights':
      return [
        { icon: Icons.lightbulb, label: 'Top insights', query: 'What are the most important open insights?' },
        { icon: Icons.users, label: 'Recurring patterns', query: 'Which customers show recurring late payment?' },
      ]
    case 'agent-hub':
      return [
        { icon: Icons.users, label: 'Which AI Employees are active?', query: 'Which AI Employees are active right now?' },
        { icon: Icons.info, label: 'Explain this page', query: 'Explain what the Agent Hub shows.' },
      ]
    case 'data-manager':
      return [
        { icon: Icons.clock, label: 'Last reset', query: 'When was the data last reset, and what does it contain?' },
        { icon: Icons.info, label: 'Explain this page', query: 'Explain what I can do in the Data Manager.' },
      ]
    default:
      return [
        { icon: Icons.helpCircle, label: 'What can you help me with?', query: 'What can you help me with?' },
        { icon: Icons.alertCircle, label: 'What needs review?', query: 'How many invoices need review right now?' },
        { icon: Icons.activity, label: 'KPI summary', query: 'Summarize the current AR KPIs.' },
      ]
  }
}

// ============================================================================
// Bubbles
// ============================================================================

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
  context?: PageContextInput
  /** Explicit prompts (for example suggested follow-ups) instead of page starters. */
  prompts?: string[]
  compact?: boolean
}

export function CapabilityBubbles({ onSelect, context, prompts, compact }: CapabilityBubblesProps) {
  const items: Starter[] = prompts
    ? prompts.map((p) => ({ icon: Icons.sparkles, label: p, query: p }))
    : startersFor(context ?? { page: '/' })

  return (
    <div className={cn('flex flex-wrap gap-2', compact ? 'justify-start p-0' : 'justify-center p-2')}>
      {items.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={`${cap.label}-${i}`}
            type="button"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 rounded-full',
              compact ? 'px-2.5 py-1.5' : 'px-3 py-2',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
